import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

type ToastKind = 'success' | 'error' | 'info' | 'warning' | 'neutral' | 'loading';
type ToastTone = 'success' | 'error' | 'info' | 'warning' | 'neutral' | 'loading';
type ToastPriority = 'low' | 'normal' | 'high' | 'urgent';
type ToastPlacement = 'top-center' | 'top-right';

type ToastOptions = {
  durationMs?: number;
  priority?: ToastPriority;
  placement?: ToastPlacement;
  dedupeKey?: string;
};

type ToastItem = {
  id: string;
  kind: ToastKind;
  message: string;
  createdAt: number;
  durationMs: number;
  priority: ToastPriority;
  placement: ToastPlacement;
  dedupeKey: string | null;
};

type ToastApi = {
  show: (kind: ToastKind, message: string, options?: ToastOptions) => void;
  success: (message: string, options?: ToastOptions) => void;
  error: (message: string, options?: ToastOptions) => void;
  warning: (message: string, options?: ToastOptions) => void;
  info: (message: string, options?: ToastOptions) => void;
  neutral: (message: string, options?: ToastOptions) => void;
  loading: (message: string, options?: ToastOptions) => void;
  dismiss: () => void;
};

const ToastContext = createContext<ToastApi | null>(null);

const TOAST_DEFAULT_DURATION_MS = 3000;
const TOAST_LEAVE_DURATION_MS = 180;
const PRIORITY_WEIGHT: Record<ToastPriority, number> = {
  low: 1,
  normal: 2,
  high: 3,
  urgent: 4
};
const TOAST_TONE_TOKEN_MAP: Record<
  ToastTone,
  {
    border: string;
    bg: string;
    iconColor: string;
    iconBg: string;
  }
> = {
  success: {
    border: '--toast-success-border',
    bg: '--toast-success-bg',
    iconColor: '--toast-success-icon-color',
    iconBg: '--toast-success-icon-bg'
  },
  error: {
    border: '--toast-error-border',
    bg: '--toast-error-bg',
    iconColor: '--toast-error-icon-color',
    iconBg: '--toast-error-icon-bg'
  },
  info: {
    border: '--toast-info-border',
    bg: '--toast-info-bg',
    iconColor: '--toast-info-icon-color',
    iconBg: '--toast-info-icon-bg'
  },
  warning: {
    border: '--toast-warning-border',
    bg: '--toast-warning-bg',
    iconColor: '--toast-warning-icon-color',
    iconBg: '--toast-warning-icon-bg'
  },
  neutral: {
    border: '--toast-neutral-border',
    bg: '--toast-neutral-bg',
    iconColor: '--toast-neutral-icon-color',
    iconBg: '--toast-neutral-icon-bg'
  },
  loading: {
    border: '--toast-loading-border',
    bg: '--toast-loading-bg',
    iconColor: '--toast-loading-icon-color',
    iconBg: '--toast-loading-icon-bg'
  }
};
const TOAST_KIND_MAP: Record<
  ToastKind,
  {
    tone: ToastTone;
    iconPath: string;
  }
> = {
  success: {
    tone: 'success',
    iconPath: 'M9.2 16.3 4.9 12l1.4-1.4 2.9 2.9 8.5-8.5 1.4 1.4z'
  },
  error: {
    tone: 'error',
    iconPath: 'm12 10.6 4-4 1.4 1.4-4 4 4 4-1.4 1.4-4-4-4 4-1.4-1.4 4-4-4-4 1.4-1.4z'
  },
  info: {
    tone: 'info',
    iconPath: 'M11 10h2v7h-2zm0-4h2v2h-2zm1-4C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2m0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16'
  },
  warning: {
    tone: 'warning',
    iconPath: 'M1.6 20h20.8L12 2zm11.4-3.1h-2v-2h2zm0-3.6h-2V8.7h2z'
  },
  neutral: {
    tone: 'neutral',
    iconPath: 'M12 2a10 10 0 1 0 .001 20.001A10 10 0 0 0 12 2m-5 9h10v2H7z'
  },
  loading: {
    tone: 'loading',
    iconPath: 'M12 4a8 8 0 0 1 7.7 5.8h-2.1A6 6 0 1 0 12 18v2a8 8 0 0 1 0-16m5.2 8.7L20 9.9V16h-6.1z'
  }
};

function uid() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function resolvePriority(kind: ToastKind, priority?: ToastPriority) {
  if (priority) return priority;
  if (kind === 'error') return 'high';
  if (kind === 'warning') return 'normal';
  if (kind === 'loading') return 'normal';
  return 'low';
}

function createToastItem(kind: ToastKind, message: string, options?: ToastOptions): ToastItem {
  const durationMs = Math.max(1200, options?.durationMs ?? TOAST_DEFAULT_DURATION_MS);
  const dedupeKey = options?.dedupeKey ? options.dedupeKey.trim() : '';
  return {
    id: uid(),
    kind,
    message: String(message || '').trim(),
    createdAt: Date.now(),
    durationMs,
    priority: resolvePriority(kind, options?.priority),
    placement: options?.placement === 'top-right' ? 'top-right' : 'top-center',
    dedupeKey: dedupeKey || null
  };
}

function compareToastPriority(a: ToastItem, b: ToastItem) {
  const delta = PRIORITY_WEIGHT[b.priority] - PRIORITY_WEIGHT[a.priority];
  if (delta !== 0) return delta;
  return a.createdAt - b.createdAt;
}

function toastIcon(kind: ToastKind) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d={TOAST_KIND_MAP[kind].iconPath} />
    </svg>
  );
}

function toastToneVars(kind: ToastKind): React.CSSProperties {
  const tone = TOAST_KIND_MAP[kind].tone;
  const tokens = TOAST_TONE_TOKEN_MAP[tone];
  return {
    '--toast-tone-border': `var(${tokens.border})`,
    '--toast-tone-bg': `var(${tokens.bg})`,
    '--toast-tone-icon-color': `var(${tokens.iconColor})`,
    '--toast-tone-icon-bg': `var(${tokens.iconBg})`
  } as React.CSSProperties;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [queue, setQueue] = useState<ToastItem[]>([]);
  const [active, setActive] = useState<ToastItem | null>(null);
  const [leavingId, setLeavingId] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);

  const dismiss = useCallback(() => {
    if (!active) return;
    setLeavingId(active.id);
  }, [active]);

  useEffect(() => {
    if (active || queue.length === 0) return;
    const sorted = [...queue].sort(compareToastPriority);
    const next = sorted[0];
    if (!next) return;
    setLeavingId(null);
    setActive(next);
    setQueue((xs) => xs.filter((item) => item.id !== next.id));
  }, [active, queue]);

  useEffect(() => {
    if (!active || !leavingId || active.id !== leavingId) return;
    const id = window.setTimeout(() => {
      setActive((current) => (current && current.id === leavingId ? null : current));
      setLeavingId((current) => (current === leavingId ? null : current));
    }, TOAST_LEAVE_DURATION_MS);
    return () => window.clearTimeout(id);
  }, [active, leavingId]);

  useEffect(() => {
    if (!active) return;
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      dismiss();
    }, active.durationMs);
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [active]);

  const show = useCallback(
    (kind: ToastKind, message: string, options?: ToastOptions) => {
      const item = createToastItem(kind, message, options);
      if (!item.message) return;
      const key = item.dedupeKey;
      const sameMessage = (target: ToastItem) => {
        if (key) return target.dedupeKey === key;
        return target.kind === item.kind && target.message === item.message;
      };
      if (active && sameMessage(active)) return;
      setQueue((xs) => {
        const withoutDup = xs.filter((target) => !sameMessage(target));
        if (active && PRIORITY_WEIGHT[item.priority] > PRIORITY_WEIGHT[active.priority]) {
          setLeavingId(null);
          setActive(item);
          return [active, ...withoutDup].slice(0, 24);
        }
        return [...withoutDup, item].slice(-24);
      });
    },
    [active]
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (m, options) => show('success', m, options),
      error: (m, options) => show('error', m, options),
      warning: (m, options) => show('warning', m, options),
      info: (m, options) => show('info', m, options),
      neutral: (m, options) => show('neutral', m, options),
      loading: (m, options) => show('loading', m, options),
      dismiss
    }),
    [dismiss, show]
  );
  const messageId = active ? `toast-message-${active.id}` : undefined;
  const tooltipId = active ? `toast-tooltip-${active.id}` : undefined;

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className={`toast-stack ${active?.placement === 'top-right' ? 'is-topRight' : 'is-topCenter'}`} aria-live="polite" aria-relevant="additions text">
        {active ? (
          <div
            key={active.id}
            id={tooltipId}
            className={`toast kind-${active.kind} ${leavingId === active.id ? 'is-leaving' : ''}`}
            style={toastToneVars(active.kind)}
            role="tooltip"
            aria-describedby={messageId}
            aria-atomic="true"
          >
            <span className="toast-icon">{toastIcon(active.kind)}</span>
            <span className="toast-message" id={messageId}>
              {active.message}
            </span>
            <button className="toast-close" type="button" onClick={dismiss} aria-label="关闭提示" aria-describedby={messageId}>
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="m12 10.6 4-4 1.4 1.4-4 4 4 4-1.4 1.4-4-4-4 4-1.4-1.4 4-4-4-4 1.4-1.4z" />
              </svg>
            </button>
          </div>
        ) : null}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    return {
      show: () => {},
      success: () => {},
      error: () => {},
      warning: () => {},
      info: () => {},
      neutral: () => {},
      loading: () => {},
      dismiss: () => {}
    };
  }
  return ctx;
}
